<script lang="ts">
  import { location, link } from "svelte-spa-router";
  import { sidebarOpen } from "../stores";
  import { CATEGORIES } from "../categories";
  import { entries, activeCategories } from "../content";
  import { isSignedIn } from "../adminStore";

  $: if ($location) sidebarOpen.set(false);

  function close() {
    sidebarOpen.set(false);
  }

  // Show only categories that have at least one SOP. Order by the canonical
  // CATEGORIES list so adding a markdown file with `category: sales` will make
  // Sales appear in the sidebar without code changes.
  $: active = activeCategories($entries);
  $: cats = CATEGORIES.filter((c) => active.includes(c.id)).sort((a, b) => a.order - b.order);

  type StaticItem = { href: string; label: string; section?: string };
  const TOP: StaticItem[] = [
    { href: "/", label: "Home" },
  ];
  const BOTTOM: StaticItem[] = [
    { href: "/search", label: "Search" },
    { href: "/changelog", label: "Changelog" },
  ];

  function isActive(href: string, current: string) {
    if (href === "/") return current === "/" || current === "";
    return current === href || current.startsWith(href + "/");
  }
</script>

{#if $sidebarOpen}
  <div
    class="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden"
    on:click={close}
    role="presentation"
  ></div>
{/if}

<aside
  class="w-60 shrink-0 border-r border-border bg-bg-panel px-3 py-5
         fixed inset-y-0 left-0 z-40 transform transition-transform duration-200
         lg:static lg:translate-x-0 lg:min-h-screen overflow-y-auto"
  class:translate-x-0={$sidebarOpen}
  class:-translate-x-full={!$sidebarOpen}
>
  <div class="px-3 mb-6 flex items-center justify-between">
    <div>
      <div class="text-text font-semibold tracking-tight">Legacy Growth</div>
      <div class="text-text-muted text-xs">Partners Playbook</div>
    </div>
    <button
      class="lg:hidden text-text-muted hover:text-text text-xl leading-none"
      on:click={close}
      aria-label="Close menu">×</button
    >
  </div>

  <nav class="flex flex-col gap-0.5">
    {#each TOP as item}
      <a
        use:link
        href={item.href}
        class="px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between"
        class:bg-bg-raised={isActive(item.href, $location)}
        class:text-text={isActive(item.href, $location)}
        class:text-text-muted={!isActive(item.href, $location)}
      >
        <span>{item.label}</span>
      </a>
    {/each}

    {#if cats.length > 0}
      <div class="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-text-dim">
        Categories
      </div>
      {#each cats as cat}
        {@const href = `/category/${cat.id}`}
        <a
          use:link
          {href}
          class="px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between"
          class:bg-bg-raised={isActive(href, $location)}
          class:text-text={isActive(href, $location)}
          class:text-text-muted={!isActive(href, $location)}
        >
          <span>{cat.label}</span>
        </a>
      {/each}
    {/if}

    <div class="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-text-dim">
      Find
    </div>
    {#each BOTTOM as item}
      <a
        use:link
        href={item.href}
        class="px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between"
        class:bg-bg-raised={isActive(item.href, $location)}
        class:text-text={isActive(item.href, $location)}
        class:text-text-muted={!isActive(item.href, $location)}
      >
        <span>{item.label}</span>
      </a>
    {/each}

    <div class="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-text-dim">
      Admin
    </div>
    <a
      use:link
      href="/admin"
      class="px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between"
      class:bg-bg-raised={isActive("/admin", $location)}
      class:text-text={isActive("/admin", $location)}
      class:text-text-muted={!isActive("/admin", $location)}
    >
      <span>{$isSignedIn ? "Dashboard" : "Sign in"}</span>
      {#if $isSignedIn}
        <span class="text-[10px] text-accent">●</span>
      {/if}
    </a>
    {#if $isSignedIn}
      <a
        use:link
        href="/admin/new"
        class="px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between"
        class:bg-bg-raised={isActive("/admin/new", $location)}
        class:text-text={isActive("/admin/new", $location)}
        class:text-text-muted={!isActive("/admin/new", $location)}
      >
        <span>+ New SOP</span>
      </a>
    {/if}
  </nav>

  <div class="mt-8 px-3 text-[10px] text-text-dim leading-relaxed">
    Press <kbd class="px-1.5 py-0.5 rounded bg-bg-raised border border-border text-[10px]">⌘ K</kbd>
    anywhere to search.
  </div>
</aside>
